import { CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MaskedLines, FadeIn } from "@/components/motion";

const inputCls =
  "h-12 rounded-xl border-olive/15 bg-cream/60 px-4 text-base text-ink placeholder:text-olive/40 focus-visible:ring-teal";

export function TextField({ id, label, required, type = "text", value, onChange, placeholder }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-semibold text-olive">
        {label} {required && <span className="text-teal">*</span>}
      </Label>
      <Input
        id={id}
        data-testid={`${id}-input`}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className={inputCls}
      />
    </div>
  );
}

export function AreaField({ id, label, required, value, onChange, placeholder, rows = 4 }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-semibold text-olive">
        {label} {required && <span className="text-teal">*</span>}
      </Label>
      <Textarea
        id={id}
        data-testid={`${id}-input`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        rows={rows}
        className="rounded-xl border-olive/15 bg-cream/60 px-4 py-3 text-base text-ink placeholder:text-olive/40 focus-visible:ring-teal"
      />
    </div>
  );
}

export function SelectField({ id, label, required, value, onChange, options, placeholder = "Select one" }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-semibold text-olive">
        {label} {required && <span className="text-teal">*</span>}
      </Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} data-testid={`${id}-select`} className={inputCls}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="bg-cream">
          {options.map((o) => (
            <SelectItem key={o} value={o} data-testid={`${id}-option-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function SubmitButton({ loading, children, testid }) {
  return (
    <button
      type="submit"
      data-testid={testid}
      disabled={loading}
      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-teal-deep px-8 py-4 text-sm font-bold text-cream transition-all duration-300 hover:bg-olive hover:shadow-lg disabled:opacity-60 sm:w-auto"
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
}

export function SuccessPanel({ title, message, onReset, testid }) {
  return (
    <FadeIn className="rounded-3xl border border-sage/40 bg-sage-mist p-10 text-center" data-testid={testid}>
      <CheckCircle2 className="mx-auto h-12 w-12 text-teal-deep" />
      <h2 className="mt-6 font-serif text-3xl text-ink">{title}</h2>
      <p className="mx-auto mt-4 max-w-md text-olive/75">{message}</p>
      {onReset && (
        <button
          onClick={onReset}
          data-testid="form-reset-btn"
          className="mt-8 rounded-full border border-teal-deep/40 px-6 py-2.5 text-sm font-semibold text-teal-deep transition-colors hover:bg-teal-deep hover:text-cream"
        >
          Submit another
        </button>
      )}
    </FadeIn>
  );
}

export function FormPage({ eyebrow, lines, intro, asideTitle, asideText, children, testid }) {
  return (
    <div data-testid={testid}>
      <section className="px-6 pb-20 pt-20 lg:px-10 lg:pb-28 lg:pt-28">
        <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <FadeIn>
              <p className="text-sm font-bold uppercase tracking-[0.25em] text-teal">{eyebrow}</p>
            </FadeIn>
            <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
              <MaskedLines lines={lines} delay={0.1} />
            </h1>
            <FadeIn delay={0.4}>
              <p className="mt-8 max-w-md text-lg leading-relaxed text-olive/75">{intro}</p>
            </FadeIn>
            {asideTitle && (
              <FadeIn delay={0.55}>
                <div className="mt-10 rounded-3xl border border-olive/10 bg-sand/60 p-8">
                  <p className="font-display italic text-xl text-teal-deep">{asideTitle}</p>
                  <p className="mt-3 text-sm leading-relaxed text-olive/75">{asideText}</p>
                </div>
              </FadeIn>
            )}
          </div>
          <div className="lg:col-span-7">
            <FadeIn delay={0.25}>
              <div className="rounded-[2rem] border border-olive/10 bg-white p-8 shadow-[0_20px_60px_-30px_rgba(43,51,43,0.25)] sm:p-10">
                {children}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
}
